import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getEmployeeEikDetails, triggerEmployeeSync } from 'src/api/employee';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { useSnackbar } from 'src/components/snackbar';
import { EmployeeSyncArea, IEmployeeEikItem } from 'src/types/employee';

type Props = {
  employeeId: string | undefined;
  employeeName: string | undefined;
  eikId: string | null | undefined;
};

type DetailsContainerProp = {
  data: IEmployeeEikItem | null;
};

function DetailsContainer({ data }: DetailsContainerProp) {
  return (
    <>
      {data ? (
        <Stack
          spacing={2}
          alignItems={{ md: 'flex-start' }}
          direction={{ xs: 'column' }}
          sx={{
            p: 2,
            pt: 0,
            fontSize: 12,
          }}
        >
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>External User Id</Box>
            {data?.externalUserId}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>UserId</Box>
            {data?.userId}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>Email</Box>
            {data?.email}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>First Name</Box>
            {data?.firstName}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>Last Name</Box>
            {data?.lastName}
          </Stack>
          <>
            {data?.organizations?.map((item, index) => (
              <Stack
                spacing={2}
                alignItems={{ md: 'flex-start' }}
                direction={{ xs: 'column' }}
                key={`org_${index}`}
                sx={{
                  width: '100%',
                }}
              >
                <Divider
                  sx={{ borderStyle: 'dotted', mt: 0, mb: 0, borderWidth: 1, width: '100%' }}
                />
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>Organization Name</Box>
                  {item.organizationName}
                </Stack>
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>Organization External Id</Box>
                  {item.externalOrganizationId}
                </Stack>
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>External Role Id</Box>
                  {item.externalRoleId}
                </Stack>
              </Stack>
            ))}
          </>
          <>
            {data?.organizations?.length === 0 ? (
              <>
                <Divider
                  sx={{ borderStyle: 'dotted', mt: 0, mb: 0, borderWidth: 1, width: '100%' }}
                />
                <Box sx={{ color: 'text.disabled' }}>No organization assigned</Box>
              </>
            ) : (
              <></>
            )}
          </>
        </Stack>
      ) : (
        <EmptyContent
          title="No Data"
          sx={{
            pt: 3,
            pb: 6,
          }}
        />
      )}
    </>
  );
}

function EikStatusBadge({ data }: DetailsContainerProp) {
  if (!data) {
    return null;
  }

  if (data.isDeactivated) {
    return (
      <Label variant="soft" color="error">
        Deactivated
      </Label>
    );
  }
  return (
    <Label variant="soft" color="success">
      Active
    </Label>
  );
}

export default function EmployeeDetailsEik({ eikId, employeeId, employeeName }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [eikData, setEikData] = useState<IEmployeeEikItem | null>(null);

  const loadData = useCallback(() => {
    if (!eikId) {
      return;
    }

    setLoading(true);
    getEmployeeEikDetails(eikId)
      .then((response) => {
        setEikData(response.data);
      })
      .catch((err) => {
        console.log(err);
        setEikData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [eikId]);

  const triggerSync = useCallback(() => {
    if (!employeeId) {
      return;
    }

    setLoading(true);
    enqueueSnackbar(`EIK sync triggered for ${employeeName}`);
    triggerEmployeeSync(employeeId, [EmployeeSyncArea.EIK])
      .then((r) => {
        enqueueSnackbar(`EIK sync finished for ${employeeName}`, {
          variant: 'success',
        });
        loadData();
      })
      .catch((err) => {
        console.log(err);
        enqueueSnackbar(`EIK sync failed due to ${err[0]?.errorKey || 'unknown error'}`, {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [enqueueSnackbar, loadData, employeeId, employeeName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Card>
      <CardHeader
        title={
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography variant="h6">EIK</Typography>
            <EikStatusBadge data={eikData} />
          </Stack>
        }
        action={
          <>
            <IconButton title="Trigger sync" onClick={() => triggerSync()}>
              <Iconify icon="mdi:account-sync" />
            </IconButton>

            <IconButton title="Refresh data" onClick={() => loadData()}>
              <Iconify icon="eva:refresh-outline" />
            </IconButton>
          </>
        }
      />
      <Divider sx={{ borderStyle: 'solid', mt: 2, mb: 2 }} />
      {loading ? (
        <Container maxWidth="lg">
          <LinearProgress key="primary" color="primary" sx={{ mb: 2, width: 1 }} />
        </Container>
      ) : (
        <DetailsContainer data={eikData} />
      )}
    </Card>
  );
}
